// ============================================================================
// COMPONENTE: STRATEGIC RADAR (V4.0 - LIST VIEW ANALYTICS)
// ARQUIVO: src/components/StrategicRadar.jsx
// OBJETIVO: Visualização em Lista/Tabela para melhor densidade de informação.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { auth } from '../shared/api/firebase/config';
import { ACADEMIA_CONSTANTS } from '../features/academy/AcademiaConstants';
import { fetchAllExamResults } from '../services/StrategicRadarRepository';
import { TrendingUp, AlertCircle, CheckCircle, Minus } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';

const FGV_WEIGHTS = {
    'ETICA': 8, 'CONST': 7, 'CIVIL': 7, 'PROC_CIVIL': 7, 'ADMIN': 6,
    'PENAL': 6, 'PROC_PENAL': 6, 'TRABALHO': 6, 'PROC_TRABALHO': 6,
    'TRIBUTARIO': 5, 'EMPRESARIAL': 5, 'HUMANOS': 2, 'INTERNACIONAL': 2,
    'ECA': 2, 'AMBIENTAL': 2, 'CONSUMIDOR': 2
};

const StrategicRadar = ({ onStartFocus, user }) => {
    const [strategyData, setStrategyData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { calculateStrategy(); }, []);

    const calculateStrategy = async () => {
        if (!user) {
            const defaultData = Object.entries(FGV_WEIGHTS)
                .sort(([,a], [,b]) => b - a)
                .map(([id, weight]) => ({
                    id, label: ACADEMIA_CONSTANTS.SUBJECTS[id]?.label || id,
                    weight, userAvg: 0, priorityScore: weight * 10, status: 'NEUTRO'
                }));
            setStrategyData(defaultData);
            setLoading(false);
            return;
        }

        try {
            const rows = await fetchAllExamResults(user.uid);
            const stats = {};

            rows.forEach((data) => {
                if (data.details) {
                    Object.entries(data.details).forEach(([subj, det]) => {
                        if (!stats[subj]) stats[subj] = { hits: 0, total: 0 };
                        stats[subj].hits += det.hits;
                        stats[subj].total += det.total;
                    });
                }
            });

            const calculated = Object.keys(FGV_WEIGHTS).map(subjectKey => {
                const weight = FGV_WEIGHTS[subjectKey];
                const userStat = stats[subjectKey] || { hits: 0, total: 0 };
                const userAvg = userStat.total > 0 ? Math.round((userStat.hits / userStat.total) * 100) : 0;
                
                // Algoritmo de Prioridade
                const priorityScore = (weight * 15) + (100 - userAvg);
                let status = 'MANUTENÇÃO';
                if (priorityScore > 180) status = 'CRÍTICO';
                else if (priorityScore > 140) status = 'ALTA';
                else if (priorityScore > 100) status = 'MÉDIA';

                return {
                    id: subjectKey,
                    label: ACADEMIA_CONSTANTS.SUBJECTS[subjectKey]?.label || subjectKey,
                    icon: ACADEMIA_CONSTANTS.SUBJECTS[subjectKey]?.icon || 'fa-book',
                    color: ACADEMIA_CONSTANTS.SUBJECTS[subjectKey]?.color || 'gray',
                    weight, userAvg, totalQuestions: userStat.total, priorityScore, status
                };
            });

            setStrategyData(calculated.sort((a,b) => b.priorityScore - a.priorityScore));
        } catch (e) { console.error("Erro Radar:", e); } finally { setLoading(false); }
    };

    const getStatusStyle = (status) => {
        switch(status) {
            case 'CRÍTICO': return { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle };
            case 'ALTA': return { bg: 'bg-orange-100', text: 'text-orange-700', icon: TrendingUp };
            case 'MÉDIA': return { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Minus };
            default: return { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle };
        }
    };

    if (loading) return <div className="text-center py-20"><i className="fas fa-satellite-dish fa-spin text-3xl text-[#C5A059]"></i></div>;

    return (
        <div className="max-w-7xl mx-auto pb-20 animate-fade-in">
            <header className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-gray-300 pb-4">
                <div>
                    <h2 className="text-2xl font-serif font-bold text-[#20243B] flex items-center gap-3">
                        <i className="fas fa-list-ol text-[#C5A059]"></i> Prioridade de Estudo
                    </h2>
                    <p className="text-gray-500 text-xs mt-1">
                        Ordenado por: <span className="font-bold">Peso na Prova</span> vs <span className="font-bold">Seu Desempenho</span>.
                    </p>
                </div>
            </header>

            <div className="hidden lg:block mb-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-300 p-4">
                    <div className="h-[320px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={strategyData}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey="label" tick={{ fontSize: 11, fill: '#20243B' }} />
                                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} />
                                <Radar dataKey="userAvg" stroke="#20243B" fill="#C5A059" fillOpacity={0.35} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* TABELA DE ALTA DENSIDADE (EXCEL-LIKE) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-300 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-100 border-b border-gray-300 text-xs font-black text-gray-500 uppercase tracking-wider">
                            <tr>
                                <th className="p-4 w-10">#</th>
                                <th className="p-4">Disciplina</th>
                                <th className="p-4 text-center">Peso (FGV)</th>
                                <th className="p-4 w-1/3">Sua Proficiência</th>
                                <th className="p-4 text-center">Prioridade</th>
                                <th className="p-4 text-right">Ação</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {strategyData.map((item, idx) => {
                                const style = getStatusStyle(item.status);
                                const StatusIcon = style.icon;
                                
                                return (
                                    <tr key={item.id} className="hover:bg-blue-50 transition-colors group">
                                        <td className="p-4 text-gray-400 font-mono text-xs">{idx + 1}</td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${item.color}-50 text-${item.color}-600 border border-${item.color}-200`}>
                                                    <i className={`fas ${item.icon}`}></i>
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-800 text-sm">{item.label}</p>
                                                    <p className="text-[10px] text-gray-400">{item.totalQuestions} questões feitas</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 text-center font-mono font-bold text-gray-600">{item.weight}</td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <span className={`text-xs font-bold w-8 ${item.userAvg >= 50 ? 'text-green-600' : 'text-red-500'}`}>{item.userAvg}%</span>
                                                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className={`h-full rounded-full ${item.userAvg >= 70 ? 'bg-green-500' : (item.userAvg >= 40 ? 'bg-yellow-400' : 'bg-red-500')}`} style={{ width: `${item.userAvg}%` }}></div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-black uppercase ${style.bg} ${style.text}`}>
                                                <StatusIcon className="w-3 h-3"/> {item.status}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <button 
                                                onClick={() => onStartFocus && onStartFocus({ materia: item.id, limit: 10 })}
                                                className="bg-white border border-gray-300 text-gray-600 hover:border-[#C5A059] hover:text-[#C5A059] px-3 py-1.5 rounded text-[10px] font-bold uppercase transition-all shadow-sm active:scale-95"
                                            >
                                                Treinar
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StrategicRadar;